import {
  Alert,
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import React from "react";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import { useParams } from "react-router-dom";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse } from "../../utils/util";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LanguageIcon from "@mui/icons-material/Language";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import BadgeIcon from "@mui/icons-material/Badge";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CreditScoreIcon from "@mui/icons-material/CreditScore";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import UpdateIcon from "@mui/icons-material/Update";

const ProjectDetail = () => {
  const { id } = useParams();
  const { data, isLoading, isError, error } = useApiEp(
    `/partner/project/${id}`
  );

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return (
      <Alert severity="error" variant="outlined">
        {getErrorResponse(error)}
      </Alert>
    );
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return "Not available";
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
      <BackBtn />
      <Typography variant="h4" gutterBottom sx={{ mt: 2, mb: 3 }}>
        Project Details
      </Typography>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <BusinessIcon color="primary" />
          <Typography variant="h5" component="h2">
            {data.name}
          </Typography>
          <Chip
            label={data.status.toUpperCase()}
            color={data.status === "active" ? "success" : "error"}
            size="small"
            sx={{ ml: 2 }}
          />
          {data.sandbox && (
            <Chip label="SANDBOX" color="warning" size="small" />
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 3,
          }}
        >
          <DetailItem
            icon={<BadgeIcon />}
            label="Project ID"
            value={data.id}
            copyable
          />
          <DetailItem
            icon={<BadgeIcon />}
            label="Business ID"
            value={data.business_id}
            copyable
          />
          <DetailItem
            icon={<BadgeIcon />}
            label="Partner ID"
            value={data.partner_id}
            copyable
          />
          <DetailItem
            icon={<ReceiptIcon />}
            label="Active Plan"
            value={data.active_plan || "None"}
          />
          <DetailItem
            icon={<ScheduleIcon />}
            label="Plan Activated On"
            value={formatDate(data.plan_activated_on)}
          />
          <DetailItem
            icon={<ScheduleIcon />}
            label="Plan Renewal On"
            value={formatDate(data.plan_renewal_on)}
          />
          <DetailItem
            icon={<CreditScoreIcon />}
            label="Credits Available"
            value={data.credit}
          />
          <DetailItem
            icon={<WhatsAppIcon />}
            label="WhatsApp Number"
            value={data.wa_number}
          />
          <DetailItem
            icon={<WhatsAppIcon />}
            label="Messaging Tier"
            value={data.wa_messaging_tier?.replace("_", " ").toUpperCase()}
          />
          <DetailItem
            icon={
              data.is_whatsapp_verified ? (
                <CheckCircleIcon color="success" />
              ) : (
                <CancelIcon color="error" />
              )
            }
            label="WhatsApp Verified"
            value={data.is_whatsapp_verified ? "Yes" : "No"}
          />
          <DetailItem
            icon={<CurrencyExchangeIcon />}
            label="Billing Currency"
            value={data.billing_currency}
          />
          <DetailItem
            icon={<LanguageIcon />}
            label="Timezone"
            value={data.timezone}
          />
          <DetailItem
            icon={<UpdateIcon />}
            label="Created At"
            value={formatDate(data.created_at)}
          />
          <DetailItem
            icon={<UpdateIcon />}
            label="Updated At"
            value={formatDate(data.updated_at)}
          />
          <DetailItem
            icon={<PhoneIcon />}
            label="Daily Template Limit"
            value={data.daily_template_limit}
          />
        </Box>

        {data.scheduled_subscription_changes && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" gutterBottom>
              Scheduled Subscription Changes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {JSON.stringify(data.scheduled_subscription_changes, null, 2)}
            </Typography>
          </>
        )}

        {data.waba_app_status &&
          Object.keys(data.waba_app_status).length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" gutterBottom>
                WABA App Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {JSON.stringify(data.waba_app_status, null, 2)}
              </Typography>
            </>
          )}
      </Paper>
    </Container>
  );
};

const DetailItem = ({ icon, label, value, copyable = false }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
    <Box sx={{ color: "text.secondary", mr: 1.5, mt: 0.5 }}>{icon}</Box>
    <Box>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
        {value}
        {copyable && (
          <Chip
            label="Copy"
            size="small"
            onClick={() => navigator.clipboard.writeText(value)}
            sx={{ ml: 1, cursor: "pointer" }}
          />
        )}
      </Typography>
    </Box>
  </Box>
);

export default ProjectDetail;
